/**
 * Invitation Service - Frontend
 * Handles activity invitations between users
 * Communicates with backend /api/invitations endpoints
 */

import { API_BASE } from '../config/api';
import AuthService from './authService';
import { notifyInvitesChanged } from '../utils/cacheInvalidation';

class InvitationService {
  static API_BASE = API_BASE;

  // Use centralized AuthService.handleResponse for auth error handling

  // =====================================================
  // SEND INVITATIONS
  // =====================================================

  /**
   * Send invitations to friends and children for an activity
   * @param {object} data - Invitation data
   * @returns {object} Result with invitation IDs
   */
  static async sendActivityInvitations(data) {
    try {
      const {
        activityId,           // Activity ID to invite to
        invitedFriendIds,     // Array of user IDs
        invitedChildIds,      // Array of child profile IDs (optional)
        customMessage         // Optional custom message
      } = data;

      const response = await fetch(`${this.API_BASE}/api/invitations`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({
          activityId,
          invitedFriendIds,
          invitedChildIds,
          customMessage
        })
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error sending invitations:', error);
      return { success: false, error: error.message };
    }
  }

  // =====================================================
  // RESPOND TO INVITATIONS
  // =====================================================

  /**
   * Respond to an activity invitation
   * @param {string} invitationId - Invitation ID
   * @param {string} response - Response: 'accepted', 'declined', 'maybe'
   * @param {string} replyMessage - Optional reply message to sender
   * @param {string} [childId] - Answer for this one child only ('child_...'):
   *   an invitation the lists answer child by child (a reopened one, or one
   *   carrying only children) names each child with its own answer
   * @returns {object} Result
   */
  static async respondToInvitation(invitationId, response, replyMessage = null, childId = null) {
    try {
      const body = { response };
      if (replyMessage) {
        body.replyMessage = replyMessage;
      }
      // snake_case on the wire (C290); the route reads child_id or childId.
      if (childId && childId.startsWith('child_')) body.child_id = childId;

      const result = await fetch(`${this.API_BASE}/api/invitations/${invitationId}/respond`, {
        method: 'PUT',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify(body)
      });

      const data = await AuthService.handleResponse(result);
      if (data?.success) notifyInvitesChanged();
      return data;
    } catch (error) {
      console.error('Error responding to invitation:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Accept or decline a trip invitation — for yourself, or for a child you
   * hold full access on (childId, 'child_...'). Trips have no 'maybe'.
   * @returns {object} { success, message? }
   */
  static async respondToTripInvitation(tripId, response, replyMessage = null, childId = null) {
    try {
      const endpoint = response === 'accepted' ? 'accept' : 'decline';
      const body = {};
      // snake_case on the wire (C304, as C290 did for activities); the trip
      // routes read child_id, else childId.
      if (childId && childId.startsWith('child_')) body.child_id = childId;
      if (replyMessage && replyMessage.trim()) body.replyMessage = replyMessage.trim();

      const result = await fetch(`${this.API_BASE}/api/trips/${tripId}/invitation/${endpoint}`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify(body)
      });

      const data = await AuthService.handleResponse(result);
      if (data?.success) notifyInvitesChanged();
      return data;
    } catch (error) {
      console.error('Error responding to trip invitation:', error);
      return { success: false, error: error.message };
    }
  }

  // =====================================================
  // GET INVITATIONS
  // =====================================================

  /**
   * Every invite still waiting on the current user — trips (their own or a
   * child's) and activities, soonest first. Read from the invitation tables,
   * not notifications, so the count matches what can actually be answered.
   * Throws on failure so the caller can keep stale data on screen.
   * @returns {array} Array of invites (see server/services/pendingInvitesService.js)
   */
  static async getPendingInvites() {
    const response = await fetch(`${this.API_BASE}/api/invitations/pending`, {
      headers: AuthService.getJsonHeaders()
    });
    const data = await AuthService.handleResponse(response);
    return data.invites || [];
  }

  /**
   * Get invitations received by current user
   * @param {string} status - Optional status filter: 'pending', 'accepted', 'declined', 'maybe'
   * @returns {array} Array of invitations
   */
  static async getReceivedInvitations(status = null) {
    try {
      const params = status ? `?status=${status}` : '';
      const response = await fetch(
        `${this.API_BASE}/api/invitations/received${params}`,
        { headers: AuthService.getJsonHeaders() }
      );

      const data = await AuthService.handleResponse(response);
      return data.invitations || [];
    } catch (error) {
      console.error('Error getting received invitations:', error);
      return [];
    }
  }

  /**
   * Get specific invitation details
   * @param {string} invitationId - Invitation ID
   * @returns {object|null} Invitation details
   */
  static async getInvitation(invitationId) {
    try {
      const response = await fetch(
        `${this.API_BASE}/api/invitations/${invitationId}`,
        { headers: AuthService.getJsonHeaders() }
      );

      const data = await AuthService.handleResponse(response);
      return data.invitation || null;
    } catch (error) {
      console.error('Error getting invitation:', error);
      return null;
    }
  }

  // =====================================================
  // ACTIVITY ATTENDEES
  // =====================================================

  /**
   * Get all attendees for an activity (organizer + attendees)
   * @param {string} activityId - Activity ID
   * @returns {array} Array of attendees
   */
  static async getActivityAttendees(activityId) {
    try {
      const response = await fetch(
        `${this.API_BASE}/api/activities/${activityId}/attendees`,
        { headers: AuthService.getJsonHeaders() }
      );

      const data = await AuthService.handleResponse(response);
      return data.attendees || [];
    } catch (error) {
      console.error('Error getting activity attendees:', error);
      return [];
    }
  }

  /**
   * Get attendees for multiple activities (batch query)
   * @param {array} activityIds - Array of activity IDs
   * @param {{ throwOnError?: boolean }} [opts] - throw instead of returning
   *   {} on failure, for callers that must tell "failed" from "nobody"
   * @returns {object} Map of activityId -> attendees array
   */
  static async getAttendeesForActivities(activityIds, { throwOnError = false } = {}) {
    try {
      if (!activityIds || activityIds.length === 0) return {};

      const response = await fetch(
        `${this.API_BASE}/api/activities/attendees/batch`,
        {
          method: 'POST',
          headers: AuthService.getJsonHeaders(),
          body: JSON.stringify({ activityIds })
        }
      );

      const data = await AuthService.handleResponse(response);
      return data.attendees || {};
    } catch (error) {
      console.error('Error getting batch attendees:', error);
      if (throwOnError) throw error;
      return {};
    }
  }

  // =====================================================
  // HELPER METHODS
  // =====================================================

  /**
   * Get pending invitations count
   * @returns {number} Number of pending invitations
   */
  static async getPendingInvitationsCount() {
    try {
      const invitations = await this.getReceivedInvitations('pending');
      return invitations.length;
    } catch (error) {
      console.error('Error getting pending count:', error);
      return 0;
    }
  }

  /**
   * Accept invitation shortcut
   * @param {string} invitationId - Invitation ID
   * @returns {object} Result
   */
  static async acceptInvitation(invitationId) {
    return await this.respondToInvitation(invitationId, 'accepted');
  }

  /**
   * Decline invitation shortcut
   * @param {string} invitationId - Invitation ID
   * @returns {object} Result
   */
  static async declineInvitation(invitationId) {
    return await this.respondToInvitation(invitationId, 'declined');
  }

  /**
   * Maybe invitation shortcut
   * @param {string} invitationId - Invitation ID
   * @returns {object} Result
   */
  static async maybeInvitation(invitationId) {
    return await this.respondToInvitation(invitationId, 'maybe');
  }

  /**
   * Remove an attendee from an activity (organizer only)
   * @param {string} activityId - Activity ID
   * @param {string} attendeeId - User ID of the attendee
   * @param {string} childId - Optional child ID if removing a child
   * @returns {object} Result
   */
  static async removeAttendee(activityId, attendeeId, childId = null) {
    try {
      const url = childId
        ? `${this.API_BASE}/api/activities/${activityId}/attendees/${attendeeId}?childId=${childId}`
        : `${this.API_BASE}/api/activities/${activityId}/attendees/${attendeeId}`;

      const response = await fetch(url, {
        method: 'DELETE',
        headers: AuthService.getJsonHeaders()
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error removing attendee:', error);
      return { success: false, error: error.message };
    }
  }
}

export default InvitationService;
